import quantize from 'quantize';
import tinycolorModule from 'tinycolor2';

interface TinyColor {
  darken(amount: number): TinyColor;
  lighten(amount: number): TinyColor;
  getBrightness(): number;
  toString(format?: string): string;
  toHsl(): { h: number; s: number; l: number };
  toHsv(): { h: number; s: number; v: number };
}

interface TinyColorStatic {
  (color: string | { h: number; s: number; l: number }): TinyColor;
  readability(color1: TinyColor | string, color2: string): number;
}

const tinycolor = tinycolorModule as unknown as TinyColorStatic;

export type RGBTuple = [number, number, number];

export type SampleRegion = 'full' | 'edges';

export interface ArtworkSampleSettings {
  colors: number;
  windowSize: number;
  sampleRegion: SampleRegion;
}

export const ARTIST_PAGE_SAMPLE: ArtworkSampleSettings = { colors: 5, windowSize: 50, sampleRegion: 'edges' };
export const RELEASE_PAGE_SAMPLE: ArtworkSampleSettings = { colors: 8, windowSize: 12, sampleRegion: 'full' };

export interface ProcessedColors {
  combinedStyle?: Record<string, string>;
  hexColors?: string[];
}

export function sampleArtworkPixels(
  data: ArrayLike<number>,
  width: number,
  height: number,
  settings: Pick<ArtworkSampleSettings, 'windowSize' | 'sampleRegion'>,
  channels = 4,
): RGBTuple[] {
  const pixels: RGBTuple[] = [];
  const edgeSize = Math.max(1, Math.floor(Math.min(width, height) * 0.18));

  for (let i = 0; i < data.length; i += channels * settings.windowSize) {
    const pixelIndex = i / channels;
    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);
    const isEdgePixel = x < edgeSize || x >= width - edgeSize || y < edgeSize || y >= height - edgeSize;

    if (settings.sampleRegion === 'edges' && !isEdgePixel) {
      continue;
    }

    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r !== undefined && g !== undefined && b !== undefined) {
      pixels.push([r, g, b]);
    }
  }

  return pixels;
}

export const rgbTupleToHex = (values: number[]) =>
  `#${values
    .map((value) => {
      const hex = value.toString(16);
      return hex.length < 2 ? `0${hex}` : hex;
    })
    .join('')}`;

export function quantizeArtworkPixels(pixels: RGBTuple[], colors: number): RGBTuple[] {
  const colorMap = quantize(pixels, colors);
  return colorMap ? colorMap.palette() : [];
}

export function artworkPaletteFromPixels(
  data: ArrayLike<number>,
  width: number,
  height: number,
  settings: ArtworkSampleSettings,
  channels = 4,
): string[] {
  return quantizeArtworkPixels(sampleArtworkPixels(data, width, height, settings, channels), settings.colors).map(rgbTupleToHex);
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const rgbToHex = (rgb: RGB): string => {
  return '#' + ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1);
};

const mapAlternateColors = (hexColors: string[]): Record<string, string> => {
  const style: Record<string, string> = {};
  hexColors.forEach((color, index) => {
    style[`--color${index + 1}`] = color;
  });
  return style;
};

const mostReadable = (backgroundColor: string, colors: string[]): string => {
  return colors
    .map((color) => ({
      color,
      readability: tinycolor.readability(backgroundColor, color),
    }))
    .sort((a, b) => b.readability - a.readability)[0].color;
};

const shadeBackgroundFromColor = (color: string, lightness: number): string => {
  const hsl = tinycolor(color).toHsl();
  const saturation = hsl.s < 0.12 ? Math.min(hsl.s, 0.04) : Math.min(Math.max(hsl.s, 0.35), 0.72);

  return tinycolor({
    h: hsl.h,
    s: saturation,
    l: lightness,
  }).toString('hex');
};

const pickThemeBaseColor = (hexColors: string[]): string => {
  const [dominantColor] = hexColors;
  const dominant = tinycolor(dominantColor);
  const dominantHsl = dominant.toHsl();
  const dominantBrightness = dominant.getBrightness();

  const dominantIsUsable = dominantHsl.s >= 0.24 && dominantBrightness >= 24 && dominantBrightness <= 190;

  if (dominantIsUsable) {
    return dominantColor;
  }

  const ranked = hexColors
    .map((color) => {
      const parsed = tinycolor(color);
      const hsl = parsed.toHsl();
      const hsv = parsed.toHsv();
      const brightness = parsed.getBrightness();
      const brightnessScore = 1 - Math.min(Math.abs(hsv.v - 0.46) / 0.46, 1);

      return {
        color,
        brightness,
        saturation: hsl.s,
        score: hsl.s * 1.35 + brightnessScore * 0.65,
      };
    })
    .filter(({ brightness, saturation }) => saturation >= 0.2 && brightness >= 18 && brightness <= 220)
    .sort((a, b) => b.score - a.score);

  return ranked[0]?.color || dominantColor;
};

const darkenBasedOnBrightness = (color: string): string => {
  const brightness = tinycolor(color).getBrightness();
  switch (true) {
    case brightness > 0 && brightness < 10:
      return tinycolor(color).darken(13).toString();
    case brightness > 10 && brightness < 20:
      return tinycolor(color).darken(11).toString();
    case brightness > 20 && brightness < 30:
    case brightness > 30 && brightness < 40:
      return tinycolor(color).darken(7).toString();
    case brightness > 40 && brightness < 50:
    case brightness > 50 && brightness < 60:
      return tinycolor(color).darken(9.5).toString();
    case brightness > 60 && brightness < 70:
    case brightness > 70 && brightness < 80:
      return tinycolor(color).darken(11).toString();
    case brightness > 80 && brightness < 90:
      return tinycolor(color).darken(13.5).toString();
    case brightness > 90 && brightness < 100:
      return tinycolor(color).darken(15).toString();
    case brightness > 100 && brightness < 110:
      return tinycolor(color).darken(25).toString();
    case brightness > 110 && brightness < 120:
      return tinycolor(color).darken(35).toString();
    default:
      return tinycolor(color).darken(0).toString();
  }
};

export function processArtworkColors(colors?: string[]): ProcessedColors {
  if (!colors || !colors.length) return { combinedStyle: undefined, hexColors: undefined };

  let hexColors = colors.map(hexToRgb).map(rgbToHex);
  const themeBaseColor = pickThemeBaseColor(hexColors);
  if (themeBaseColor !== hexColors[0]) {
    hexColors = [themeBaseColor, ...hexColors.filter((color) => color.toLowerCase() !== themeBaseColor.toLowerCase())];
  }
  const brightness = tinycolor(hexColors[0]).getBrightness();

  if (brightness < 60) {
    hexColors[1] = shadeBackgroundFromColor(hexColors[0], 0.18);
    hexColors[2] = shadeBackgroundFromColor(hexColors[0], 0.1);
    hexColors[3] = shadeBackgroundFromColor(hexColors[0], 0.15);
    hexColors[4] = shadeBackgroundFromColor(hexColors[0], 0.18);
  }

  if (brightness >= 60) {
    hexColors[2] = tinycolor(hexColors[2]).darken(18).toString();
    hexColors[4] = tinycolor(hexColors[4]).darken(18).toString();
  }

  hexColors.push(tinycolor(hexColors[0]).darken(7).toString());
  hexColors.push(tinycolor(hexColors[0]).darken(14).toString());

  const mappedStyle = mapAlternateColors(hexColors);

  const accentColor = (() => {
    const mainColor = tinycolor(hexColors[0]);
    const hsl = mainColor.toHsl();
    const hueShift = mainColor.getBrightness() < 50 ? 30 : 180;

    let accent = tinycolor({
      h: (hsl.h + hueShift) % 360,
      s: Math.min(hsl.s * 1.2, 1),
      l: Math.min(hsl.l * 1.1, 0.6),
    });

    if (accent.getBrightness() < 30) {
      accent = accent.lighten(20);
    } else if (accent.getBrightness() > 225) {
      accent = accent.darken(20);
    }

    return accent.toString('hex');
  })();

  const scrollbarTrackColor = darkenBasedOnBrightness(hexColors[4]);
  const bgColorBase = hexColors[4] === '#ffffff' || hexColors[4] === '#000000' ? hexColors[0] : hexColors[4];
  const backgroundColor =
    brightness < 60
      ? shadeBackgroundFromColor(hexColors[0], 0.12)
      : tinycolor(darkenBasedOnBrightness(bgColorBase)).darken(1).toString();
  const backgroundColor2 =
    brightness < 60
      ? shadeBackgroundFromColor(hexColors[0], 0.08)
      : tinycolor(darkenBasedOnBrightness(bgColorBase)).darken(10).toString();

  let logoColor = tinycolor(hexColors[0]).lighten(28).toString();
  let logoColor2 = tinycolor(hexColors[0]).lighten(56).toString();

  if (tinycolor.readability(backgroundColor2, logoColor) < 4.5) {
    logoColor =
      tinycolor.readability(backgroundColor2, logoColor2) >= 4.5
        ? logoColor2
        : mostReadable(backgroundColor2, [logoColor, logoColor2, '#ffffff', '#000000']);
  }

  if (tinycolor.readability(hexColors[4], logoColor2) < 4.5) {
    logoColor2 = mostReadable(hexColors[4], [logoColor2, logoColor, '#ffffff', '#000000']);
  }

  const sidebarColor =
    brightness < 49 ? shadeBackgroundFromColor(hexColors[0], 0.22) : tinycolor(hexColors[0]).lighten(0).toString();
  const sidebarHeaderColor = brightness > 70 ? '#000' : tinycolor(logoColor).lighten(15).toString();

  const textColor = (() => {
    const mainColor = tinycolor(hexColors[0]);
    const bgColor = tinycolor(backgroundColor);
    return mostReadable(bgColor.toString(), [
      mainColor.lighten(45).toString(),
      mainColor.darken(45).toString(),
      logoColor,
      logoColor2,
      '#ffffff',
      '#000000',
    ]);
  })();
  const sidebarTextColor = mostReadable(sidebarColor, [logoColor, logoColor2, textColor, '#ffffff', '#000000']);
  const sidebarAccentColor = mostReadable(sidebarColor, [logoColor2, logoColor, textColor, '#000000', '#ffffff']);
  const sidebarSurfaceBase =
    sidebarTextColor === '#000000'
      ? tinycolor(sidebarColor).lighten(12).toString()
      : tinycolor(sidebarColor).darken(12).toString();
  const sidebarSurfaceColor =
    tinycolor.readability(sidebarSurfaceBase, sidebarTextColor) >= 4.5
      ? sidebarSurfaceBase
      : sidebarTextColor === '#000000'
        ? tinycolor(sidebarColor).lighten(22).toString()
        : tinycolor(sidebarColor).darken(22).toString();
  const sidebarSurfaceTextColor = mostReadable(sidebarSurfaceColor, [sidebarTextColor, '#ffffff', '#000000']);

  const combinedStyle = {
    ...mappedStyle,
    '--logoColor': logoColor,
    '--logoColor2': logoColor2,
    '--sidebarColor': sidebarColor,
    '--sidebarHeaderColor': sidebarHeaderColor,
    '--sidebarTextColor': sidebarTextColor,
    '--sidebarAccentColor': sidebarAccentColor,
    '--sidebarSurfaceColor': sidebarSurfaceColor,
    '--sidebarSurfaceTextColor': sidebarSurfaceTextColor,
    '--textColor': textColor,
    '--scrollbarTrackColor': scrollbarTrackColor,
    '--backgroundColor': backgroundColor,
    '--backgroundColor2': backgroundColor2,
    '--accentColor': accentColor,
  };

  return { combinedStyle, hexColors };
}

export function paletteCss(style: Record<string, string>): string {
  const declarations = Object.entries(style)
    .filter(([name, value]) => /^--[a-zA-Z0-9-]+$/.test(name) && /^[#a-zA-Z0-9(),.%\s]+$/.test(value))
    .map(([name, value]) => `${name}:${value}`)
    .join(';');
  return `:root{${declarations}}`;
}
